/**
 * LogPermitActivityModal — log / edit a Permit Activity, or set a review's status.
 *
 * Opens via CustomEvent('open-log-permit-activity', { detail }):
 *   log:       { reviewId, reviewName }
 *   edit:      { reviewId, reviewName, activityId, prefill: { type, date, remarks, plannedNext, plannedNextDate } }
 *   setStatus: { reviewId, reviewName, mode: 'setStatus', currentStatus }
 *
 * Logging sets the agency-review Status in-app from PERMIT_ACTIVITY_TO_STATUS
 * (see services/permits.js). On success it busts the activity + review caches and
 * dispatches outline-render (plus a short delayed reload as a safety net).
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import {
  logPermitActivity, updatePermitActivity, setReviewStatus,
  PERMIT_ACTIVITY_TYPES, PERMIT_PLANNED_NEXT,
} from '../services/permits.js'
import Select from './Select.jsx'
import { getRecentReviewActivity, reportActivitiesLogged, activityPlain, getClientReach } from '../services/permitting.js'
import { confirmClientUpdate } from './confirmClientUpdate.js'

const TYPE_OPTIONS = PERMIT_ACTIVITY_TYPES.map(t => ({ value: t, label: t }))
const NEXT_OPTIONS = [{ value: '', label: '— None —' }].concat(PERMIT_PLANNED_NEXT.map(t => ({ value: t, label: t })))
const REVIEW_STATUSES = [
  'Preparing to Submit', 'Submitted - Intake in Process', 'Items Requested', 'Under Review',
  'Making Corrections', 'Preparing for Hearing', 'Appeal Period', 'Approved',
  'Permit Revision - Under Review', 'Permit Revision - Making Corrections',
  'Permit Revision - Approved', 'Cancelled',
]

function todayIso() { return new Date().toISOString().slice(0, 10) }

function bustAndReload(reviewId) {
  if (ctx.permitActivityCache) ctx.permitActivityCache[reviewId] = undefined
  ctx.permitReviewCache = undefined
  ctx.permitCache = undefined
  window.dispatchEvent(new Event('outline-render'))
  setTimeout(() => {
    ctx.permitReviewCache = undefined
    ctx.permitCache = undefined
    window.dispatchEvent(new Event('outline-render'))
  }, 1800)
}

// Logged from the hearing card instead (docs/HEARING_OUTCOMES.md "One entry either way").
const HEARING_OUTCOME_TYPES = ['👍 Received Committee Approval', '⏭️ Received Hearing Continuance']

export default function LogPermitActivityModal() {
  const [open,       setOpen]       = useState(false)
  const [mode,       setMode]       = useState('log')   // 'log' | 'edit' | 'setStatus'
  const [reviewId,   setReviewId]   = useState(null)
  const [reviewName, setReviewName] = useState('')
  const [permitId,   setPermitId]   = useState(null)
  const [activityId, setActivityId] = useState(null)
  const [actType,    setActType]    = useState('')
  const [actDate,    setActDate]    = useState('')
  const [remarks,    setRemarks]    = useState('')
  const [nextAct,    setNextAct]    = useState('')
  const [nextDate,   setNextDate]   = useState('')
  const [statusVal,  setStatusVal]  = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)
  // Duplicate guard (Matt, 2026-09-23): posting a submission / Agency Review milestone now logs the
  // activity itself, so before logging by hand the coordinator is shown the same event already on
  // this review (and how it got there) and has to confirm it is a separate one.
  const [recent,     setRecent]     = useState(null)   // null = loading; { activities, dupWindowDays }
  const [dupOk,      setDupOk]      = useState(false)
  // 'email' | 'silent' — silent still updates the client's Permitting page, but emails no one
  // (the same choice as posting a milestone silently).
  const [sendChoice, setSendChoice] = useState('email')
  // The same event already on this review near this date — however it got there.
  const duplicate = (mode === 'log' && recent && actType && actDate)
    ? (recent.activities || []).find(a => a.type === actType && a.date &&
        Math.abs((new Date(a.date + 'T00:00:00Z') - new Date(actDate + 'T00:00:00Z')) / 86400000) <= (recent.dupWindowDays || 21))
    : null

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setMode(d.mode === 'setStatus' ? 'setStatus' : (d.activityId ? 'edit' : 'log'))
      setReviewId(d.reviewId || null)
      setReviewName(d.reviewName || 'Agency Review')
      setPermitId(d.permitId || null)
      setActivityId(d.activityId || null)
      setActType(d.prefill?.type || '')
      setActDate(d.prefill?.date || todayIso())
      setRemarks(d.prefill?.remarks || '')
      setNextAct(d.prefill?.plannedNext || '')
      setNextDate(d.prefill?.plannedNextDate || '')
      setStatusVal(d.currentStatus || '')
      setError(null); setSubmitting(false); setOpen(true)
      setDupOk(false); setRecent(null); setSendChoice('email')
      if (d.reviewId && d.mode !== 'setStatus' && !d.activityId) {
        getRecentReviewActivity(d.reviewId).then(setRecent).catch(() => setRecent({ activities: [], dupWindowDays: 21 }))
      }
    }
    window.addEventListener('open-log-permit-activity', onOpen)
    return () => window.removeEventListener('open-log-permit-activity', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    if (!reviewId) return
    setSubmitting(true); setError(null)
    try {
      if (mode === 'setStatus') {
        if (!statusVal) { setSubmitting(false); return }
        await setReviewStatus(reviewId, statusVal)
      } else if (mode === 'edit') {
        if (!actType || !actDate) { setSubmitting(false); return }
        const f = { 'Activity Type': actType, 'Date': actDate, 'Remarks': remarks || '' }
        f['Planned Next Activity'] = nextAct || null
        f['Planned Next Activity Date'] = (nextAct && nextDate) ? nextDate : null
        await updatePermitActivity(activityId, f)
      } else {
        if (!actType || !actDate) { setSubmitting(false); return }
        if (duplicate && !dupOk) { setSubmitting(false); return }
        // The client gets this update, worded exactly as written — show it and have them confirm.
        let reachInfo = null
        try { reachInfo = await getClientReach({ reviewId, activityType: actType }) }
        catch { reachInfo = { hasJourney: true, subscribers: 0, held: false, clientLabel: activityPlain(actType) } }   // can't tell: warn anyway
        const silent = sendChoice === 'silent'
        const ok = await confirmClientUpdate({ reach: reachInfo, memo: remarks, silent,
          confirmLabel: silent ? 'Log and update silently' : 'Log and send to client' })
        if (!ok) { setSubmitting(false); return }
        const created = await logPermitActivity(reviewId, actType, {
          date:               actDate,
          remarks:            remarks || undefined,
          recordedByMemberId: wf.currentMemberId || undefined,
          plannedNext:        nextAct || undefined,
          plannedNextDate:    (nextAct && nextDate) ? nextDate : undefined,
          permitId:           permitId || undefined,
        })
        // The client's Permitting page updates and subscribers get the memo by email (server side).
        // Best-effort: the activity is logged either way.
        if (created && created.id) {
          const m = ctx.currentMember
          reportActivitiesLogged({ activityIds: [created.id], source: 'manual', silent, memberName: m?.fields?.['Member Name'] || m?.fields?.['Name'] || null })
            .catch(e => console.error('[permits] client update failed (non-fatal):', e && e.message))
        }
      }
      setOpen(false)
      bustAndReload(reviewId)
    } catch (ex) {
      setError(ex.message || 'Failed to save'); setSubmitting(false)
    }
  }

  if (!open) return null

  const isStatus = mode === 'setStatus'
  const canSubmit = (isStatus ? !!statusVal : (actType && actDate && !(duplicate && !dupOk))) && !submitting
  const title = isStatus ? 'Set Review Status' : (mode === 'edit' ? 'Edit Activity' : 'Log Permit Activity')
  const cta = isStatus ? 'Set Status' : (mode === 'edit' ? 'Save' : 'Log Activity')

  const labelStyle = {
    display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
    color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
  }
  const inputStyle = {
    width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
    color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, boxSizing: 'border-box', outline: 'none',
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="LogPermitActivityModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{title}</div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{reviewName}</div>
          </div>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isStatus ? (
            <div>
              <label style={labelStyle}>Status</label>
              <Select value={statusVal} onChange={setStatusVal} options={REVIEW_STATUSES.map(s => ({ value: s, label: s }))}
                placeholder="Select status…" style={{ width: '100%', fontSize: 12 }} />
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4 }}>
                Manual override — sets the review status directly without logging an activity.
              </div>
            </div>
          ) : (
            <>
              <div>
                <label style={labelStyle}>Activity Type <span style={{ color: '#c0392b' }}>*</span></label>
                <Select value={actType} onChange={v => { setActType(v); setDupOk(false) }} options={TYPE_OPTIONS} placeholder="Select event…" style={{ width: '100%', fontSize: 12 }} />
                {mode === 'log' && (
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4 }}>
                    Logging this event sets the agency-review status in-app.
                  </div>
                )}
                {mode === 'log' && /Submitted to Agency|Resubmitted to Agency/.test(actType) && (
                  <button type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-plan-check-letter', { detail: { reviewId, reviewName } }))}
                    style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-accent-blue)', textDecoration: 'underline' }}>
                    Make the plan check cover letter for this submittal
                  </button>
                )}
              </div>
              <div>
                <label style={labelStyle}>Date <span style={{ color: '#c0392b' }}>*</span></label>
                <input type="date" style={inputStyle} value={actDate} onChange={e => setActDate(e.target.value)} />
              </div>
              {mode === 'log' && HEARING_OUTCOME_TYPES.includes(actType) && (
                <div style={{ padding: '9px 11px', borderRadius: 6, border: '0.5px solid var(--aj-accent, #07272D)', background: 'rgba(7,39,45,0.05)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', lineHeight: 1.45 }}>
                  <div style={{ fontWeight: 600, marginBottom: 3 }}>This is a hearing outcome</div>
                  Record it on the hearing card instead. It logs this entry on the permit, sets the hearing date, posts the hearing milestones, starts the appeal clock, makes each condition a thread, and drafts the client email and the board thank-you for you to read before they go.
                  <div style={{ marginTop: 7 }}>
                    <button type="button" onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('open-hearing-outcome', { detail: { reviewId } })) }}
                      style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 14, border: 'none', background: 'var(--aj-accent, #07272D)', color: '#fff', cursor: 'pointer' }}>Record the hearing outcome</button>
                  </div>
                </div>
              )}
              {duplicate && (
                <div style={{ padding: '9px 11px', borderRadius: 6, border: '0.5px solid #b8540a', background: 'rgba(184,84,10,0.07)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', lineHeight: 1.45 }}>
                  <div style={{ fontWeight: 600, color: '#b8540a', marginBottom: 3 }}>Already logged</div>
                  {activityPlain(duplicate.type)} was logged on {new Date(duplicate.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  {duplicate.source === 'milestone_post'
                    ? <> when <strong>{duplicate.milestoneName || 'its milestone'}</strong> was posted.</>
                    : duplicate.source === 'hearing_outcome'
                      ? <> when the hearing outcome was recorded{duplicate.loggedBy ? <> by <strong>{duplicate.loggedBy}</strong></> : null}. It is already on the permit.</>
                      : '.'}
                  {duplicate.memo ? <div style={{ color: 'var(--aj-text-mid)', marginTop: 4, fontStyle: 'italic' }}>"{String(duplicate.memo).slice(0, 160)}{String(duplicate.memo).length > 160 ? '…' : ''}"</div> : null}
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 7, cursor: 'pointer' }}>
                    <input type="checkbox" checked={dupOk} onChange={e => setDupOk(e.target.checked)} /> This is a separate event. Log it anyway.
                  </label>
                </div>
              )}
              <div>
                <label style={labelStyle}>Remarks · the client sees this</label>
                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="What happened, in a sentence or two for the client." />
                {mode === 'log' && (
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4 }}>
                    Goes on the client's Permitting page{sendChoice === 'silent' ? '.' : ' and in their email update.'}
                  </div>
                )}
              </div>
              {mode === 'log' && (
                <div>
                  <label style={labelStyle}>Client Hub</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { v: 'email',  t: 'Update and email', d: 'Shows on the client’s Permitting page. Subscribers get an email.' },
                      { v: 'silent', t: 'Update silently',  d: 'Shows on the client’s Permitting page. No email goes to subscribers.' },
                    ].map(opt => {
                      const active = sendChoice === opt.v
                      return (
                        <label key={opt.v} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                          padding: '8px 10px', borderRadius: 6,
                          border: `0.5px solid ${active ? '#1D9E75' : 'var(--aj-border)'}`,
                          background: active ? 'rgba(29,158,117,0.06)' : 'transparent',
                        }}>
                          <input type="radio" name="permitSendChoice" checked={active} onChange={() => setSendChoice(opt.v)}
                            style={{ marginTop: 2, flexShrink: 0, accentColor: '#1D9E75', cursor: 'pointer' }} />
                          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', lineHeight: 1.4 }}>
                            {opt.t}
                            <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{opt.d}</div>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Planned Next Activity</label>
                  <Select value={nextAct} onChange={setNextAct} options={NEXT_OPTIONS} placeholder="— None —" style={{ width: '100%', fontSize: 12 }} />
                </div>
                {nextAct && (
                  <div style={{ width: 150 }}>
                    <label style={labelStyle}>Next Date</label>
                    <input type="date" style={inputStyle} value={nextDate} onChange={e => setNextDate(e.target.value)} />
                  </div>
                )}
              </div>
            </>
          )}

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default', transition: 'background 0.12s' }}>
              {submitting ? 'Saving…' : cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
